import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
// Client-side data layer for MCP connections (user-connected external MCP
// servers). The screen is intentionally read-only + delete: CONNECTING a
// server is a redirect/OAuth dance, so that path is handed off to chat (the
// assistant has the manage-mcp tool). Here we only list what's already
// connected and let the user remove one.

// Mirror of the VMR mcp-connection Status enum, narrowed to what the UI shows.
export type McpStatus = 'PENDING_AUTH' | 'CONNECTED' | 'EXPIRED' | 'REVOKED' | 'ERROR_STATE' | 'UNKNOWN_STATUS';

export interface McpConnectionSummary {
  id: string;
  name?: string;
  url?: string;
  status: McpStatus;
  lastError?: string;
  toolCount?: number;
  /** Set for a ready-made connector connected by slug. The ONLY thing that pairs such a connection with
   *  its registry entry — its `url` holds the vendor API base a pre-gateway connect wrote, so matching by
   *  url silently fails and the same integration renders twice: as its own card and as a raw MCP row. */
  connectorSlug?: string;
}

// Human label + whether it reads as healthy (drives the status dot color).
export function mcpStatusLabel(status: McpStatus, lastError?: string): { label: string; healthy: boolean } {
  switch (status) {
    case 'CONNECTED':
      // A good credential whose vendor reports the account's switches off: the service records the
      // refusal on the row. Not healthy, because the dot is what the user reads, and "Connected" on its
      // own is the claim this exists to stop.
      return lastError?.trim() ? { label: 'Connected, not usable yet', healthy: false } : { label: 'Connected', healthy: true };
    case 'PENDING_AUTH':
      return { label: 'Awaiting authorization', healthy: false };
    case 'EXPIRED':
      return { label: 'Authorization expired', healthy: false };
    case 'REVOKED':
      return { label: 'Access revoked', healthy: false };
    case 'ERROR_STATE':
      return { label: 'Connection error', healthy: false };
    default:
      return { label: 'Unknown', healthy: false };
  }
}

export async function fetchMcpConnections(): Promise<McpConnectionSummary[]> {
  const res = await apiFetch(`${API_BASE}/api/mcp/connections`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  const data = (await res.json()) as { connections?: McpConnectionSummary[] };
  return data.connections ?? [];
}

export async function deleteMcpConnection(id: string): Promise<void> {
  const res = await apiFetch(`${API_BASE}/api/mcp/connections/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
}

async function readErrorMessage(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { error?: string; message?: string };
    return data.error || data.message || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}
